<template>
  <div class="animated fadeIn pvp-banlist-view">
    <div class="main-container">
      <b-card no-body class="border-0 shadow-sm mb-3 bg-dark text-white overflow-hidden polished-card">
        <b-card-body class="p-4">
          <div class="banlist-header">
            <div class="banlist-title-compact">
              <div class="filter-bar">
                <div class="filter-title">
                  <span>Quick Filters</span>
                  <small>Pick a rule type</small>
                </div>
                <div class="filter-group">
                  <button
                    v-for="type in ruleTypeFilters"
                    :key="type.value"
                    type="button"
                    class="filter-pill"
                    :class="{ active: activeRuleType === type.value }"
                    @click="setRuleTypeFilter(type.value)"
                  >
                    <i v-if="type.icon" :class="type.icon"></i>
                    <span>{{ type.label }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="banlist-toolbar">
              <div class="controls-row">
                <div class="controls-panel controls-panel-grow controls-panel-actions">
                  <div class="controls-label">Actions</div>
                  <div class="action-row">
                    <b-button
                      v-if="hasPermission(PvpBanlistEdit)"
                      variant="danger"
                      size="sm"
                      class="mr-2"
                      @click="openAddModal"
                    >
                      <i class="fa fa-plus mr-1"></i> Add Rule
                    </b-button>

                    <b-input-group size="sm" class="search-box-group action-search">
                      <b-input-group-prepend class="search-box-prepend">
                        <i class="fa fa-search"></i>
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="quickFilter"
                        placeholder="Pokemon, item, moves, message..."
                        class="search-box-input"
                      />
                      <b-input-group-append v-if="quickFilter">
                        <b-button variant="outline-secondary" class="search-clear" @click="quickFilter = ''">
                          <i class="fa fa-times small"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>

                    <b-button variant="outline-secondary" size="sm" class="ml-2" @click="refresh">
                      <i class="fa fa-refresh" :class="{'fa-spin': loading}"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <div class="rule-list-wrapper">
        <div v-if="loading" class="loading-overlay">
          <b-spinner label="Loading..." variant="primary"></b-spinner>
          <div class="text-muted mt-2">Loading rules...</div>
        </div>

        <div v-if="!filteredEntries.length && !loading" class="text-center py-5 text-muted">
          <i class="fa fa-ban fa-3x mb-3 opacity-20"></i>
          <div class="h5">No banlist rules found.</div>
        </div>

        <b-row v-else class="rule-grid">
        <b-col
          v-for="entry in filteredEntries"
          :key="entry.id"
          cols="12"
          md="6"
          xl="4"
          class="mb-3"
        >
          <b-card no-body class="rule-card border-0 shadow-sm bg-dark text-white h-100">
            <div class="rule-card-header">
              <span class="rule-badge" :class="badgeClass(entry)">{{ getRuleType(entry) }}</span>
              <span class="rule-id">#{{ entry.id }}</span>
              <div class="d-flex align-items-center">
                <b-button
                  v-if="hasPermission(PvpBanlistEdit)"
                  variant="link"
                  class="rule-action-btn"
                  @click="openEditModal(entry)"
                  title="Edit Rule"
                >
                  <i class="fa fa-pencil"></i>
                </b-button>
                <b-button
                  v-if="hasPermission(PvpBanlistEdit)"
                  variant="link"
                  class="rule-action-btn"
                  :disabled="deletingId === entry.id"
                  @click="confirmDelete(entry)"
                  title="Delete Rule"
                >
                  <i class="fa" :class="deletingId === entry.id ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
                </b-button>
              </div>
            </div>

            <div class="rule-title">{{ getRuleSummary(entry) }}</div>
            <div class="rule-explain">{{ getRuleExplanation(entry) }}</div>

            <div class="rule-details">
              <div class="rule-row">
                <span class="rule-label">Applies to</span>
                <span class="rule-value">
                  <span class="rule-icon" v-if="entry.dex_id1">
                    <img
                      :src="getPokemonSpriteUrl(entry.dex_id1)"
                      :alt="getTargetName(entry)"
                      :data-fallback-id="getPokemonFallbackId(entry.dex_id1)"
                      @error="handleIconError"
                    />
                    <i class="fa fa-question" aria-hidden="true"></i>
                  </span>
                  <span class="rule-icon rule-icon-any" v-else>
                    <i class="fa fa-globe" aria-hidden="true"></i>
                  </span>
                  {{ getTargetName(entry) }}
                </span>
              </div>
              <div v-if="entry.dex_id2" class="rule-row">
                <span class="rule-label">Team requires</span>
                <span class="rule-value">
                  <span class="rule-icon">
                    <img
                      :src="getPokemonSpriteUrl(entry.dex_id2)"
                      :alt="getTeammateName(entry)"
                      :data-fallback-id="getPokemonFallbackId(entry.dex_id2)"
                      @error="handleIconError"
                    />
                    <i class="fa fa-question" aria-hidden="true"></i>
                  </span>
                  {{ getTeammateName(entry) }}
                </span>
              </div>
              <div v-if="hasRequirements(entry)" class="rule-row">
                <span class="rule-label">Requires</span>
                <span class="rule-value">
                  <span v-if="entry.held_item" class="rule-icon">
                    <img
                      :src="getItemSpriteUrl(entry)"
                      :alt="getItemName(entry)"
                      @error="handleIconError"
                    />
                    <i class="fa fa-question" aria-hidden="true"></i>
                  </span>
                  <span v-if="entry.ability" class="rule-icon rule-icon-meta">
                    <i class="fa fa-bolt" aria-hidden="true"></i>
                  </span>
                  <span v-if="hasMoveRequirements(entry)" class="rule-icon rule-icon-meta">
                    <i class="fa fa-crosshairs" aria-hidden="true"></i>
                  </span>
                  {{ getRequirementsText(entry) }}
                </span>
              </div>
              <div class="rule-row">
                <span class="rule-label">In-Game Message</span>
                <span class="rule-value">{{ stripColorTags(entry.message || 'No message') }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
        </b-row>
      </div>
    </div>

    <b-modal
      v-model="showAddModal"
      size="lg"
      scrollable
      hide-footer
      header-bg-variant="dark"
      header-text-variant="white"
      body-bg-variant="darker"
      content-class="border-0 shadow-lg bg-dark rounded-lg"
    >
      <template #modal-title>
        <span class="font-weight-bold">{{ editingId ? 'Edit' : 'Add' }} PvP Banlist Rule</span>
      </template>

      <div class="p-3 add-modal-body">
        <div class="preview-card mb-4">
          <div class="preview-title">Preview</div>
          <div class="preview-summary">{{ formSummary }}</div>
          <div class="preview-explain">{{ formExplanation }}</div>
          <div class="preview-details">
            <div class="preview-row">
              <span class="preview-label">Applies to</span>
              <span class="preview-value">{{ formPokemonLabel }}</span>
            </div>
            <div v-if="form.pokemonList[1]" class="preview-row">
              <span class="preview-label">Team requires</span>
              <span class="preview-value">{{ formTeammateLabel }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Requires</span>
              <span class="preview-value">{{ formRequirementsLabel }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">In-Game Message</span>
              <span class="preview-value">{{ formatMessagePreview(form.message) || 'Required' }}</span>
            </div>
          </div>
        </div>

        <div class="form-panel" :class="{ 'is-invalid': invalidShake }">
          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="Pokemon (ban target)" label-class="form-label">
                <div class="moves-list">
                  <div
                    v-for="(pokemon, index) in form.pokemonList"
                    :key="`pokemon-${index}`"
                    class="move-row"
                  >
                    <div class="list-field">
                      <search-pokemon
                        :values="monsters"
                        v-model="form.pokemonList[index]"
                        :placeholder="index === 0 ? 'Pokemon' : 'Teammate'"
                        :class="{ 'is-invalid': pokemonStates[index] === false }"
                        @change="onPokemonChange(index, $event)"
                        @selectedItem="onPokemonSelected(index, $event)"
                      ></search-pokemon>
                    </div>
                    <b-button
                      variant="link"
                      size="sm"
                      class="ml-2 mini-icon-btn"
                      :disabled="form.pokemonList.length === 1"
                      @click="removePokemon(index)"
                    >
                      <i class="fa fa-minus"></i>
                    </b-button>
                  </div>
                  <b-button
                    variant="link"
                    size="sm"
                    class="mt-2 action-link"
                    :disabled="form.pokemonList.length >= 2"
                    @click="addPokemon"
                  >
                    <i class="fa fa-plus"></i>
                    <span>Add teammate</span>
                  </b-button>
                </div>
                <b-form-invalid-feedback v-if="pokemonHasError" class="d-block">
                  Select a valid Pokemon from the list.
                </b-form-invalid-feedback>
                <small class="form-hint">Add teammate only for combo bans.</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12" md="4">
              <b-form-group label="Ability" label-class="form-label" :state="abilityState">
                <div class="autocomplete-shell" :class="{'no-results': !abilityItems.length, 'inactive': abilityQuery.length < 2}">
                  <v-autocomplete
                    :min-len="2"
                    :wait="120"
                    :items="abilityItems"
                    v-model="form.ability"
                    :get-label="getAbilityLabel"
                    :auto-select-one-item="false"
                    placeholder="Type ability"
                    :class="{ 'is-invalid': abilityState === false }"
                    @update-items="updateAbilityItems"
                    @change="onAbilityChange"
                    @item-selected="onAbilitySelected"
                  >
                  </v-autocomplete>
                </div>
                <b-form-invalid-feedback v-if="abilityState === false" class="d-block">
                  Use a valid ability from the list.
                </b-form-invalid-feedback>
                <small class="form-hint">Only if ability matches.</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Nature" label-class="form-label" :state="natureState">
                <b-form-select v-model="form.nature" :options="natureOptions" :state="natureState"></b-form-select>
                <b-form-invalid-feedback v-if="natureState === false">
                  Use a valid nature from the list.
                </b-form-invalid-feedback>
                <small class="form-hint">Only if nature matches.</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Held Item" label-class="form-label" :state="heldItemState">
                <search-item
                  :values="items"
                  v-model="form.heldItemName"
                  placeholder="Search item..."
                  :class="{ 'is-invalid': heldItemState === false }"
                ></search-item>
                <b-form-invalid-feedback v-if="heldItemState === false" class="d-block">
                  Select a valid item from the list.
                </b-form-invalid-feedback>
                <small class="form-hint">Only if item matches.</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="Moves (all required)" label-class="form-label">
                <div class="moves-list">
                  <div
                    v-for="(move, index) in form.moves"
                    :key="`move-${index}`"
                    class="move-row"
                  >
                    <div class="list-field">
                      <search-pokemon
                        :values="moves"
                        v-model="form.moves[index]"
                        :placeholder="`Move ${index + 1}`"
                        :class="{ 'is-invalid': moveStates[index] === false }"
                        @change="onMoveChange(index, $event)"
                        @selectedItem="onMoveSelected(index, $event)"
                      ></search-pokemon>
                    </div>
                    <b-button
                      variant="link"
                      size="sm"
                      class="ml-2 mini-icon-btn"
                      :disabled="form.moves.length === 1"
                      @click="removeMove(index)"
                    >
                      <i class="fa fa-minus"></i>
                    </b-button>
                  </div>
                  <b-button
                    variant="link"
                    size="sm"
                    class="mt-2 action-link"
                    :disabled="form.moves.length >= 4"
                    @click="addMove"
                  >
                    <i class="fa fa-plus"></i>
                    <span>Add move</span>
                  </b-button>
                </div>
                <b-form-invalid-feedback v-if="movesHaveError" class="d-block">
                  Select valid moves from the list.
                </b-form-invalid-feedback>
                <small class="form-hint">All listed moves must match.</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="form-row">
            <b-col cols="12">
              <b-form-group label="In-Game Message (required)" label-class="form-label" :state="messageState">
                <b-form-textarea v-model="form.message" rows="2" no-resize placeholder="Shown to player on block" required :state="messageState"></b-form-textarea>
                <b-form-invalid-feedback v-if="messageState === false">
                  Message is required.
                </b-form-invalid-feedback>
                <small class="form-hint">Shown to player. Color tag added.</small>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </div>

      <div class="p-3 border-top border-dark-accent d-flex justify-content-end">
        <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showAddModal = false">Cancel</b-button>
        <b-button variant="danger" size="sm" :disabled="saving" :class="{ 'btn-disabled-soft': !isFormValid && !saving }" @click="submitRule">
          <i class="fa" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
          <span class="ml-1">{{ saving ? 'Saving...' : 'Save Rule' }}</span>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Monsters from '@/views/pokemon/_monsters'
import Moves from '@/views/pokemon/_moves'
import Natures from '@/views/pokemon/_natures'
import Abilities from '@/views/pokemon/_abilities'
import Items from '@/views/user/inventory/_items'
import SearchPokemon from '@/views/pokemon/Search'
import SearchItem from '@/views/user/inventory/Search'
import { mapGetters } from 'vuex'

export default {
  name: 'PvpBanlist',
  components: {
    'search-pokemon': SearchPokemon,
    'search-item': SearchItem
  },
  data() {
    return {
      quickFilter: '',
      activeServer: 'gold',
      loading: false,
      saving: false,
      deletingId: null,
      editingId: null,
      invalidShake: false,
      validationActive: false,
      abilityItems: [],
      abilityQuery: '',
      pokemonInputs: [''],
      moveInputs: [''],
      activeRuleType: 'all',
      ruleTypeFilters: [
        { label: 'All', value: 'all', icon: 'fa fa-th-large' },
        { label: 'Pokemon', value: 'pokemon', icon: 'fa fa-user-circle' },
        { label: 'Item', value: 'item', icon: 'fa fa-gift' },
        { label: 'Ability', value: 'ability', icon: 'fa fa-bolt' },
        { label: 'Team Combo', value: 'team', icon: 'fa fa-users' }
      ],
      showAddModal: false,
      monsters: Object.freeze(Monsters),
      moves: Object.freeze(Moves),
      natures: Object.freeze(Natures),
      abilities: Object.freeze(Abilities),
      cachedAbilities: [],
      items: Object.freeze(Items),
      form: {
        pokemonList: [''],
        ability: '',
        nature: '',
        heldItemName: '',
        moves: [''],
        message: ''
      }
    }
  },
  computed: {
    ...mapGetters(['pvpBanlistEntries']),
    banlistEntries() {
      return this.pvpBanlistEntries || []
    },
    filteredEntries() {
      const query = this.quickFilter.trim().toLowerCase()
      return this.banlistEntries.filter(entry => {
        const matchesQuery = !query || this.buildSearchText(entry).toLowerCase().includes(query)
        if (!matchesQuery) return false

        if (this.activeRuleType === 'pokemon' && !entry.dex_id1) return false
        if (this.activeRuleType === 'item' && !entry.held_item) return false
        if (this.activeRuleType === 'ability' && !entry.ability) return false
        if (this.activeRuleType === 'team' && !entry.dex_id2) return false

        return true
      })
    },
    stats() {
      const entries = this.banlistEntries
      const moveLocks = entries.filter(entry => (
        entry.attack_id1 || entry.attack_id2 || entry.attack_id3 || entry.attack_id4
      )).length
      const itemLocks = entries.filter(entry => entry.held_item).length
      const notes = entries.filter(entry => entry.message && entry.message.trim().length > 0).length
      return {
        total: entries.length,
        moveLocks,
        itemLocks,
        notes
      }
    },
    formPokemonLabel() {
      if (!this.form.pokemonList[0]) return 'Any Pokemon'
      return this.getLookupName(this.form.pokemonList[0], this.monsterMapName, this.monsterMapId)
    },
    formTeammateLabel() {
      if (!this.form.pokemonList[1]) return '-'
      return this.getLookupName(this.form.pokemonList[1], this.monsterMapName, this.monsterMapId)
    },
    formItemLabel() {
      const value = (this.form.heldItemName || '').trim()
      if (!value) return ''
      return this.getItemLabelFromName(value)
    },
    formMovesLabel() {
      return this.form.moves
        .filter(move => move !== '' && move !== null && move !== undefined)
        .map(move => this.getLookupName(move, this.moveMapName, this.moveMapId))
    },
    formRequirementsLabel() {
      const parts = []
      if (this.form.ability.trim()) parts.push(`Ability: ${this.form.ability.trim()}`)
      if (this.form.nature.trim()) parts.push(`Nature: ${this.form.nature.trim()}`)
      if ((this.form.heldItemName || '').trim()) parts.push(`Item: ${this.formItemLabel}`)
      const moves = this.formMovesLabel
      if (moves.length) parts.push(`Move/s: ${moves.join(', ')}`)
      return parts.length ? parts.join(' | ') : 'None'
    },
    formSummary() {
      const target = this.formPokemonLabel
      const teammate = this.formTeammateLabel
      const item = (this.form.heldItemName || '').trim() ? this.formItemLabel : ''
      const moves = this.formMovesLabel
      const ability = this.form.ability.trim()
      const nature = this.form.nature.trim()

      const hasTarget = !!this.form.pokemonList[0]
      const hasTeammate = !!this.form.pokemonList[1]
      const hasExtra = !!item || !!ability || !!nature || moves.length > 0

      if (hasTarget && !hasTeammate && !hasExtra) return `${target} is banned in PvP.`
      if (!hasTarget && item && !hasTeammate && !ability && !nature && !moves.length) return `${item} is banned on any Pokemon.`
      if (hasTarget && item && !hasTeammate && !ability && !nature && !moves.length) return `${target} holding ${item} is banned.`
      if (hasTarget && hasTeammate && !hasExtra) return `${target} and ${teammate} cannot be on the same team.`

      let base = hasTarget ? `${target} is banned` : 'Any Pokemon is banned'
      const parts = []
      if (hasTeammate) parts.push(`when teamed with ${teammate}`)
      if (ability) parts.push(`ability is ${ability}`)
      if (nature) parts.push(`nature is ${nature}`)
      if (item) parts.push(`holding ${item}`)
      if (moves.length) parts.push(`moves include ${moves.join(', ')}`)
      if (parts.length) base += ` when ${parts.join(', and ')}`
      return `${base}.`
    },
    formExplanation() {
      const parts = []
      if (this.form.pokemonList[1]) parts.push('team must contain the teammate')
      if (this.form.ability.trim()) parts.push('ability matches')
      if (this.form.nature.trim()) parts.push('nature matches')
      if ((this.form.heldItemName || '').trim()) parts.push('held item matches')
      if (this.formMovesLabel.length) parts.push('all moves are present')
      if (!parts.length) return 'This is a direct ban without extra requirements.'
      return `Rule triggers only if ${parts.join(', ')}.`
    },
    natureOptions() {
      return [
        { value: '', text: 'Select nature...' },
        ...this.natures.map(nature => ({ value: nature, text: nature }))
      ]
    },
    pokemonValidation() {
      return this.form.pokemonList.map((value, index) => (
        this.validateSelectedLookup(
          value,
          this.pokemonInputs[index],
          this.monsterMapName,
          this.monsterMapId,
          'Pokemon'
        )
      ))
    },
    pokemonStates() {
      if (!this.validationActive) return this.pokemonValidation.map(() => null)
      return this.pokemonValidation.map(result => (result.valid ? null : false))
    },
    pokemonHasError() {
      if (!this.validationActive) return false
      return this.pokemonValidation.some(result => !result.valid)
    },
    moveValidation() {
      return this.form.moves.map((value, index) => (
        this.validateSelectedLookup(
          value,
          this.moveInputs[index],
          this.moveMapName,
          this.moveMapId,
          'Move'
        )
      ))
    },
    moveStates() {
      if (!this.validationActive) return this.moveValidation.map(() => null)
      return this.moveValidation.map(result => (result.valid ? null : false))
    },
    movesHaveError() {
      if (!this.validationActive) return false
      return this.moveValidation.some(result => !result.valid)
    },
    heldItemValidation() {
      return this.validateLookup(this.form.heldItemName, this.itemMapName, this.itemMapId, 'Item')
    },
    heldItemState() {
      if (!this.validationActive) return null
      return this.heldItemValidation.valid ? null : false
    },
    abilityValidation() {
      const raw = String(this.form.ability || '').trim()
      if (!raw) return { valid: true }
      if (!this.abilitySet) return { valid: true }
      const isValid = this.abilitySet.has(raw.toLowerCase())
      return { valid: isValid, message: 'Use a valid ability from the list.' }
    },
    abilityState() {
      if (!this.validationActive) return null
      return this.abilityValidation.valid ? null : false
    },
    natureValidation() {
      const raw = String(this.form.nature || '').trim()
      if (!raw) return { valid: true }
      if (!this.natureSet) return { valid: true }
      const isValid = this.natureSet.has(raw.toLowerCase())
      return { valid: isValid, message: 'Use a valid nature from the list.' }
    },
    natureState() {
      if (!this.validationActive) return null
      return this.natureValidation.valid ? null : false
    },
    messageValidation() {
      const hasMessage = String(this.form.message || '').trim().length > 0
      return { valid: hasMessage, message: 'Message is required.' }
    },
    messageState() {
      if (!this.validationActive) return null
      return this.messageValidation.valid ? null : false
    },
    firstInvalidMessage() {
      const validations = [
        this.heldItemValidation,
        this.abilityValidation,
        this.natureValidation,
        ...this.pokemonValidation,
        ...this.moveValidation,
        this.messageValidation
      ]
      const first = validations.find(result => result && result.valid === false)
      return first ? (first.message || 'Please correct the highlighted fields.') : ''
    },
    isFormValid() {
      const validations = [
        this.heldItemValidation,
        this.abilityValidation,
        this.natureValidation,
        ...this.pokemonValidation,
        ...this.moveValidation,
        this.messageValidation
      ]
      return validations.every(result => result.valid !== false)
    },
  },
  watch: {
    pvpBanlistEntries() {
      this.loading = false
      this.saving = false
      this.deletingId = null
      this.editingId = null
    }
  },
  created() {
    this.buildLookups()
    this.refresh()
  },
  methods: {
    buildLookups() {
      this.itemMapName = new Map()
      this.itemMapId = new Map()
      if (this.items) {
        this.items.forEach(i => {
          this.itemMapName.set(i.Name.toLowerCase(), i)
          this.itemMapId.set(i.ID, i)
        })
      }

      this.monsterMapName = new Map()
      this.monsterMapId = new Map()
      if (this.monsters) {
        this.monsters.forEach(m => {
          this.monsterMapName.set(m.Name.toLowerCase(), m)
          this.monsterMapId.set(m.ID, m)
        })
      }

      this.moveMapName = new Map()
      this.moveMapId = new Map()
      if (this.moves) {
        this.moves.forEach(m => {
          this.moveMapName.set(m.Name.toLowerCase(), m)
          this.moveMapId.set(m.ID, m)
        })
      }

      this.natureSet = new Set()
      if (this.natures) {
        this.natures.forEach(nature => {
          this.natureSet.add(String(nature).toLowerCase())
        })
      }

      this.abilitySet = new Set()
      if (this.abilities) {
        this.abilities.forEach(ability => {
          this.abilitySet.add(String(ability).toLowerCase())
        })
      }
      this.cachedAbilities = (this.abilities || []).map(ability => ({
        item: ability,
        name: String(ability || '').toLowerCase()
      }))
    },
    getAbilityLabel(item) {
      if (item === null || item === undefined) return ''
      return String(item)
    },
    updateAbilityItems(text) {
      const search = String(text || '').toLowerCase().trim()
      this.abilityQuery = search
      if (search.length < 2) {
        this.abilityItems = []
        return
      }
      const result = []
      const maxResults = 8
      for (let i = 0; i < this.cachedAbilities.length; i++) {
        if (this.cachedAbilities[i].name.includes(search)) {
          result.push(this.cachedAbilities[i].item)
          if (result.length >= maxResults) break
        }
      }
      this.abilityItems = result
    },
    onAbilityChange(item) {
      let value = ''
      if (item && item.target && typeof item.target.value === 'string') {
        value = item.target.value
      } else if (item !== null && item !== undefined) {
        value = String(item)
      }
      const rawValue = String(value || '')
      this.abilityQuery = rawValue.toLowerCase().trim()
      if (this.abilityQuery.length < 2) {
        this.abilityItems = []
      }
      this.form.ability = rawValue
    },
    onAbilitySelected(item) {
      const value = item ? String(item) : ''
      this.abilityQuery = value.toLowerCase().trim()
      this.abilityItems = []
      this.form.ability = value
    },
    refresh() {
      this.loading = true
      this.emit('getPvpBanlist', this.activeServer)
    },
    openAddModal() {
      this.resetForm()
      this.showAddModal = true
    },
    openEditModal(entry) {
      this.editingId = entry.id
      this.invalidShake = false
      this.validationActive = false
      this.form = {
        pokemonList: [
           entry.dex_id1 || '',
           entry.dex_id2 || ''
        ].filter(id => id !== '' && id !== 0),
        ability: entry.ability || '',
        nature: entry.nature || '',
        heldItemName: this.getItemName(entry) || '',
        moves: [
          entry.attack_id1,
          entry.attack_id2,
          entry.attack_id3,
          entry.attack_id4
        ].filter(id => id > 0),
        message: this.stripColorTags(entry.message || '')
      }
      this.abilityItems = []
      this.abilityQuery = String(this.form.ability || '').toLowerCase().trim()
      this.pokemonInputs = this.form.pokemonList.map(() => '')
      this.moveInputs = this.form.moves.map(() => '')
      if (this.form.pokemonList.length === 0) this.form.pokemonList = ['']
      if (this.form.moves.length === 0) this.form.moves = ['']
      if (this.form.pokemonList.length === 1 && this.pokemonInputs.length === 0) this.pokemonInputs = ['']
      if (this.form.moves.length === 1 && this.moveInputs.length === 0) this.moveInputs = ['']
      this.showAddModal = true
    },
    resetForm() {
      this.editingId = null
      this.invalidShake = false
      this.validationActive = false
      this.form = {
        pokemonList: [''],
        ability: '',
        nature: '',
        heldItemName: '',
        moves: [''],
        message: ''
      }
      this.abilityItems = []
      this.abilityQuery = ''
      this.pokemonInputs = ['']
      this.moveInputs = ['']
    },
    submitRule() {
      this.validationActive = true
      if (!this.isFormValid) {
        this.triggerInvalidAnimation()
        this.showInfoModal('Invalid Fields', this.firstInvalidMessage || 'Please correct the highlighted fields.')
        return
      }
      const payload = this.buildPayload()
      if (!payload) return
      this.saving = true
      this.loading = true
      
      if (this.editingId) {
        payload.id = this.editingId
        this.emit('editPvpBanlist', payload)
      } else {
        this.emit('addPvpBanlist', payload)
      }
      this.showAddModal = false
    },
    addMove() {
      if (this.form.moves.length >= 4) return
      this.form.moves.push('')
      this.moveInputs.push('')
    },
    addPokemon() {
      if (this.form.pokemonList.length >= 2) return
      this.form.pokemonList.push('')
      this.pokemonInputs.push('')
    },
    removePokemon(index) {
      if (this.form.pokemonList.length <= 1) return
      this.form.pokemonList.splice(index, 1)
      this.pokemonInputs.splice(index, 1)
    },
    removeMove(index) {
      if (this.form.moves.length <= 1) return
      this.form.moves.splice(index, 1)
      this.moveInputs.splice(index, 1)
    },
    confirmDelete(entry) {
      if (!entry || !entry.id) return
      this.$bvModal.msgBoxConfirm(`Delete rule #${entry.id}?`, {
        title: 'Delete Rule',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel'
      }).then(value => {
        if (value) {
          this.deletingId = entry.id
          this.loading = true
          this.emit('deletePvpBanlist', { id: entry.id, server: this.activeServer })
        }
      })
    },
    triggerInvalidAnimation() {
      this.invalidShake = true
      setTimeout(() => {
        this.invalidShake = false
      }, 420)
    },
    normalizeInputValue(value) {
      if (value && value.target && typeof value.target.value === 'string') {
        return value.target.value
      }
      if (value === null || value === undefined) return ''
      return String(value)
    },
    onPokemonChange(index, value) {
      this.$set(this.pokemonInputs, index, this.normalizeInputValue(value))
    },
    onPokemonSelected(index) {
      this.$set(this.pokemonInputs, index, '')
    },
    onMoveChange(index, value) {
      this.$set(this.moveInputs, index, this.normalizeInputValue(value))
    },
    onMoveSelected(index) {
      this.$set(this.moveInputs, index, '')
    },
    validateSelectedLookup(value, rawInput, mapName, mapId, label) {
      const raw = String(rawInput || '').trim()
      const selectedEmpty = value === null || value === undefined || value === '' || value === 0
      if (raw && selectedEmpty) {
        return { valid: false, message: `${label} must be selected from the list.` }
      }
      return this.validateLookup(value, mapName, mapId, label)
    },
    validateLookup(value, mapName, mapId, label) {
      const raw = value === null || value === undefined ? '' : value
      if (raw === '' || raw === 0) return { valid: true, empty: true }
      const numeric = parseInt(raw, 10)
      if (!Number.isNaN(numeric)) {
        const exists = mapId ? mapId.has(numeric) : true
        return exists ? { valid: true } : { valid: false, message: `${label} must be selected from the list.` }
      }
      const key = String(raw || '').toLowerCase().trim()
      if (!key) return { valid: true, empty: true }
      const exists = mapName ? mapName.has(key) : true
      return exists ? { valid: true } : { valid: false, message: `${label} must be selected from the list.` }
    },
    buildPayload() {
      const dexId1 = this.normalizeId(this.form.pokemonList[0], 'Pokemon')
      if (dexId1 === null) return null
      const dexId2 = this.normalizeId(this.form.pokemonList[1], 'Teammate')
      if (dexId2 === null) return null
      const heldItem = this.resolveItemId(this.form.heldItemName)
      if (heldItem === null) return null

      const moveIds = []
      for (let i = 0; i < this.form.moves.length; i += 1) {
        const moveId = this.normalizeId(this.form.moves[i], `Move ${i + 1}`)
        if (moveId === null) return null
        moveIds.push(moveId)
      }

      return {
        server: this.activeServer,
        dex_id1: dexId1,
        dex_id2: dexId2,
        held_item: heldItem,
        ability: this.form.ability.trim(),
        nature: this.form.nature.trim(),
        attack_id1: moveIds[0] || 0,
        attack_id2: moveIds[1] || 0,
        attack_id3: moveIds[2] || 0,
        attack_id4: moveIds[3] || 0,
        message: this.wrapMessage(this.form.message.trim())
      }
    },
    resolveItemId(name) {
      const value = (name || '').trim()
      if (!value) return 0
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) return numeric
      const found = this.itemMapName.get(value.toLowerCase())
      if (!found) {
        this.showInfoModal('Invalid Item', `Unknown item: ${value}`)
        return null
      }
      return found.ID
    },
    normalizeId(value, label) {
      if (value === null || value === undefined || value === '') return 0
      if (typeof value === 'number') return value
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) return numeric
      this.showInfoModal('Invalid Value', `${label} must be selected from the list.`)
      return null
    },
    buildSearchText(entry) {
      return [
        this.getRuleSummary(entry),
        this.getRuleExplanation(entry),
        this.getTargetName(entry),
        this.getTeammateName(entry),
        this.getRequirementsText(entry),
        this.stripColorTags(entry.message || '')
      ].join(' ')
    },
    getRuleType(entry) {
      const hasTarget = entry.dex_id1 > 0
      const hasTeammate = entry.dex_id2 > 0
      const hasItem = entry.held_item > 0
      const hasAbility = !!entry.ability
      const hasNature = !!entry.nature
      const hasMoves = this.getMoves(entry).length > 0
      const hasExtra = hasItem || hasAbility || hasNature || hasMoves

      if (hasTarget && !hasTeammate && !hasExtra) return 'Pokemon Ban'
      if (!hasTarget && hasItem && !hasTeammate && !hasAbility && !hasNature && !hasMoves) return 'Item Ban'
      if (hasTarget && hasItem && !hasTeammate && !hasAbility && !hasNature && !hasMoves) return 'Pokemon + Item Ban'
      if (hasTarget && hasTeammate && !hasExtra) return 'Team Combo Ban'
      return 'Conditional Ban'
    },
    badgeClass(entry) {
      const type = this.getRuleType(entry)
      if (type === 'Pokemon Ban') return 'rule-badge-danger'
      if (type === 'Item Ban') return 'rule-badge-success'
      if (type === 'Pokemon + Item Ban') return 'rule-badge-warning'
      if (type === 'Team Combo Ban') return 'rule-badge-info'
      return 'rule-badge-primary'
    },
    getRuleSummary(entry) {
      const target = this.getTargetName(entry)
      const teammate = this.getTeammateName(entry)
      const item = this.getItemName(entry)
      const moves = this.getMoves(entry)
      const ability = entry.ability
      const nature = entry.nature

      const type = this.getRuleType(entry)
      if (type === 'Pokemon Ban') return `${target} is banned in PvP.`
      if (type === 'Item Ban') return `${item} is banned on any Pokemon.`
      if (type === 'Pokemon + Item Ban') return `${target} holding ${item} is banned.`
      if (type === 'Team Combo Ban') return `${target} and ${teammate} cannot be on the same team.`

      let base = target === 'Any Pokemon' ? 'Any Pokemon is banned' : `${target} is banned`
      const parts = []
      if (teammate !== '-') parts.push(`when teamed with ${teammate}`)
      if (ability) parts.push(`ability is ${ability}`)
      if (nature) parts.push(`nature is ${nature}`)
      if (item) parts.push(`holding ${item}`)
      if (moves.length) parts.push(`moves include ${moves.join(', ')}`)
      if (parts.length) {
        base += ` when ${parts.join(', and ')}`
      }
      return base + '.'
    },
    getRuleExplanation(entry) {
      const moves = this.getMoves(entry)
      const parts = []
      if (entry.dex_id2) parts.push('team must contain the teammate')
      if (entry.ability) parts.push('ability matches')
      if (entry.nature) parts.push('nature matches')
      if (entry.held_item) parts.push('held item matches')
      if (moves.length) parts.push('all moves are present')
      if (!parts.length) return 'This is a direct ban without extra requirements.'
      return `Rule triggers only if ${parts.join(', ')}.`
    },
    hasRequirements(entry) {
      return !!entry.ability || !!entry.nature || !!entry.held_item || this.getMoves(entry).length > 0
    },
    getTargetName(entry) {
      if (entry.monster1) return entry.monster1
      if (entry.dex_id1) return `#${entry.dex_id1}`
      return 'Any Pokemon'
    },
    getTeammateName(entry) {
      if (!entry.dex_id2) return '-'
      if (entry.monster2) return entry.monster2
      return `#${entry.dex_id2}`
    },
    getItemName(entry) {
      if (entry.held_item_name) return entry.held_item_name
      if (entry.held_item && this.itemMapId) {
        const found = this.itemMapId.get(entry.held_item)
        if (found) return found.Name
      }
      if (entry.held_item) return `#${entry.held_item}`
      return ''
    },
    getPokemonSpriteUrl(id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric)) return ''
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${numeric}.png`
    },
    getItemSpriteUrl(entry) {
      const name = this.getItemName(entry)
      return this.getItemSpriteUrlByName(name)
    },
    getItemSpriteUrlByName(name) {
      if (!name) return ''
      const slug = this.slugifyItemName(name)
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${slug}.png`
    },
    getPokemonFallbackId(id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric) || !this.monsterMapId) return ''
      const current = this.monsterMapId.get(numeric)
      if (!current || !current.Name) return ''
      const baseName = this.normalizePokemonName(current.Name)
      if (!baseName || baseName === current.Name) return ''
      const base = this.monsterMapName.get(baseName.toLowerCase())
      if (!base || base.ID === id) return ''
      return base.ID
    },
    normalizePokemonName(name) {
      const raw = String(name || '')
      if (raw.startsWith('Rotom-') || raw.startsWith('Rotom ')) {
        return 'Rotom'
      }
      return raw
        .replace(/\b(Alola|Alolan|Galarian|Hisuian|Paldean|Mega|Primal|Origin|Therian|Incarnate|Forme?|Mode|Sky|Midnight|Dusk|Dawn|Ultra|Gigantamax|Gmax|Fan|Wash|Heat|Frost|Mow)\b/gi, '')
        .replace(/\b(Attack|Defense|Speed|Complete|Ash|Black|White)\b/gi, '')
        .replace(/[-:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    slugifyItemName(name) {
      return String(name || '')
        .toLowerCase()
        .replace(/'/g, '')
        .replace(/\./g, '')
        .replace(/:/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
    },
    handleIconError(event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.getPokemonSpriteUrl(fallbackId)
        target.dataset.fallbackId = ''
        return
      }
      target.style.display = 'none'
    },
    getMoves(entry) {
      const list = [
        { name: entry.attack_name1, id: entry.attack_id1 },
        { name: entry.attack_name2, id: entry.attack_id2 },
        { name: entry.attack_name3, id: entry.attack_id3 },
        { name: entry.attack_name4, id: entry.attack_id4 }
      ]
      return list
        .filter(move => move.name || move.id)
        .map(move => (move.name && move.name.length ? move.name : `#${move.id}`))
    },
    getRequirementsText(entry) {
      const parts = []
      if (entry.ability) parts.push(`Ability: ${entry.ability}`)
      if (entry.nature) parts.push(`Nature: ${entry.nature}`)
      if (entry.held_item) parts.push(`Item: ${this.getItemName(entry)}`)
      const moves = this.getMoves(entry)
      if (moves.length) parts.push(`Move/s: ${moves.join(', ')}`)
      return parts.length ? parts.join(' | ') : 'None'
    },
    hasMoveRequirements(entry) {
      return this.getMoves(entry).length > 0
    },
    wrapMessage(message) {
      if (!message) return ''
      const cleaned = this.stripColorTags(message).trim()
      return cleaned ? `[FF0000]${cleaned}[-]` : ''
    },
    stripColorTags(message) {
      return String(message || '').replace(/\[[0-9a-fA-F]{6}\]|\[-\]/g, '').trim()
    },
    formatMessagePreview(message) {
      return this.stripColorTags(message)
    },
    getLookupName(value, mapName, mapId) {
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) {
        const byId = mapId.get(numeric)
        return byId ? `${byId.Name} (#${numeric})` : `#${numeric}`
      }
      const byName = mapName.get(value.toLowerCase())
      return byName ? `${byName.Name} (#${byName.ID})` : value
    },
    getItemLabelFromName(value) {
      const numeric = parseInt(value, 10)
      if (!Number.isNaN(numeric)) return `#${numeric}`
      const found = this.itemMapName.get(value.toLowerCase())
      if (!found) return value
      return `${found.Name} (#${found.ID})`
    },
    showInfoModal(title, message) {
      this.$bvModal.msgBoxOk(message, {
        title: title,
        okVariant: 'primary',
        okTitle: 'OK'
      })
    },
    setRuleTypeFilter(value) {
      this.activeRuleType = this.activeRuleType === value ? 'all' : value
    }
  }
}
</script>

<style scoped>
.pvp-banlist-view .polished-card {
  border-radius: 12px;
}

.pvp-banlist-view .bg-danger-soft {
  background: rgba(231, 76, 60, 0.14);
}

.pvp-banlist-view .shadow-sm-glow {
  box-shadow: 0 0 14px rgba(231, 76, 60, 0.22);
}

.pvp-banlist-view .search-box-wrapper {
  border-radius: 8px;
}

.search-box-group {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
}

.search-box-prepend {
  background: #12161b;
  border: 0;
  color: rgba(255, 255, 255, 0.55);
  padding: 0 12px;
  display: flex;
  align-items: center;
}

.search-box-input {
  background: #12161b;
  border: 0;
  color: #fff;
  font-weight: 600;
  padding: 8px 10px;
}

.search-box-input::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.search-clear {
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
  color: rgba(255, 255, 255, 0.6);
}

.banlist-header {
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: driftIn 0.35s ease;
}

.banlist-title-compact {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.banlist-icon {
  background: rgba(231, 76, 60, 0.12);
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 0 18px rgba(231, 76, 60, 0.2);
  animation: pulseGlow 2.6s ease-in-out infinite;
}

.banlist-toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.controls-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(16, 19, 23, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.04);
  min-width: 180px;
  animation: fadeSlide 0.3s ease;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.controls-panel:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.2);
}

.controls-panel-grow {
  flex: 1 1 260px;
  min-width: 240px;
}

.controls-panel-actions {
  min-width: 360px;
}

.controls-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 700;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-row .btn {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.action-row .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 14px rgba(0, 0, 0, 0.25);
}

.btn-disabled-soft {
  opacity: 0.6;
}

.action-search {
  flex: 1 1 auto;
  min-width: 220px;
}

@media (min-width: 992px) {
  .banlist-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .banlist-toolbar {
    min-width: 480px;
  }

  .controls-row {
    flex-wrap: nowrap;
    justify-content: flex-end;
    gap: 16px;
  }
}

.rule-list-wrapper {
  position: relative;
  min-height: 120px;
  padding: 10px 0 2px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 12, 15, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
  animation: fadeIn 0.2s ease-in;
  pointer-events: all;
}

.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(16, 19, 23, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  min-width: 320px;
  animation: fadeSlide 0.35s ease;
}

.filter-title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 700;
}

.filter-title small {
  font-size: 0.65rem;
  text-transform: none;
  letter-spacing: normal;
  color: rgba(255, 255, 255, 0.4);
}

.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: rgba(24, 28, 34, 0.6);
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
}

.filter-pill:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.filter-pill.active {
  background: rgba(32, 168, 216, 0.18);
  color: #ccefff;
  border-color: rgba(32, 168, 216, 0.6);
  box-shadow: 0 0 12px rgba(32, 168, 216, 0.2);
}

.rule-grid {
  margin: 0;
}

.rule-card {
  border-radius: 12px;
  padding: 16px 18px 18px;
  background: linear-gradient(145deg, rgba(27, 31, 36, 0.95), rgba(22, 25, 30, 0.95));
  animation: riseIn 0.35s ease;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rule-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.3);
}

.rule-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.rule-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.rule-badge-danger {
  background: rgba(231, 76, 60, 0.2);
  color: #f9c0b8;
}

.rule-badge-success {
  background: rgba(40, 167, 69, 0.2);
  color: #b6f1c1;
}

.rule-badge-warning {
  background: rgba(255, 193, 7, 0.2);
  color: #ffe4a1;
}

.rule-badge-info {
  background: rgba(23, 162, 184, 0.2);
  color: #b3eef7;
}

.rule-badge-primary {
  background: rgba(32, 168, 216, 0.2);
  color: #bfe9f6;
}

.rule-id {
  color: rgba(255, 255, 255, 0.45);
  font-weight: 600;
  margin-left: auto;
  margin-right: 8px;
}

.rule-action-btn {
  color: rgba(255, 255, 255, 0.5);
  padding: 0 4px;
}

.rule-action-btn:hover {
  color: #f86c6b;
}

.rule-title {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.rule-explain {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 14px;
}

.rule-details {
  display: grid;
  gap: 8px;
}

.rule-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.rule-label {
  width: 120px;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.45);
  margin-right: 8px;
}

.rule-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rule-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: linear-gradient(150deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  position: relative;
}

.rule-icon img {
  width: 22px;
  height: 22px;
  image-rendering: pixelated;
}

.rule-icon i {
  position: absolute;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.rule-icon-any i,
.rule-icon-meta i {
  opacity: 1;
  position: static;
}

.rule-icon-meta {
  background: linear-gradient(150deg, rgba(32, 168, 216, 0.28), rgba(32, 168, 216, 0.08));
  color: #bfe9f6;
  box-shadow: inset 0 0 0 1px rgba(32, 168, 216, 0.25);
}

.rule-icon-meta i {
  color: rgba(191, 233, 246, 0.95);
}

.rule-icon-any {
  background: linear-gradient(150deg, rgba(248, 108, 107, 0.2), rgba(248, 108, 107, 0.06));
  box-shadow: inset 0 0 0 1px rgba(248, 108, 107, 0.25);
}

.rule-icon-any i {
  color: rgba(248, 108, 107, 0.95);
}

.rule-icon img[src=""] {
  display: none;
}

.rule-icon img[style*="display: none"] + i,
.rule-icon img[style*="display:none"] + i {
  opacity: 1;
}

.preview-card {
  background: rgba(24, 28, 34, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px 16px;
}

.preview-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
}

.preview-summary {
  font-weight: 700;
  margin-bottom: 4px;
}

.preview-explain {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 10px;
}

.preview-details {
  display: grid;
  gap: 6px;
}

.preview-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.preview-label {
  width: 120px;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.45);
  margin-right: 8px;
}

.preview-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
}

.moves-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.move-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.list-field {
  flex: 1 1 auto;
  min-width: 0;
}

.move-row .btn {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.move-row .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.25);
}

.add-modal-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  --form-field-bg: #2a3137;
  --form-field-border: rgba(255, 255, 255, 0.12);
}

.form-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-row {
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.form-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.form-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}

.form-panel .form-control,
.form-panel .custom-select {
  background: var(--form-field-bg);
  border: 1px solid var(--form-field-border);
  color: #fff;
}

.form-panel textarea.form-control {
  background: var(--form-field-bg) !important;
}

.form-panel .form-control::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.form-panel ::v-deep .v-autocomplete input {
  background: var(--form-field-bg) !important;
  border: 1px solid var(--form-field-border) !important;
  color: #fff !important;
  border-radius: 8px;
  min-height: 38px;
  padding: 0.375rem 0.75rem;
  width: 100%;
}

.form-panel ::v-deep .v-autocomplete input::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.form-panel .form-control:focus,
.form-panel .custom-select:focus,
.form-panel ::v-deep .v-autocomplete input:focus {
  border-color: rgba(231, 76, 60, 0.55);
  box-shadow: 0 0 0 0.15rem rgba(231, 76, 60, 0.2);
  outline: none;
}

.form-panel .autocomplete-shell.no-results ::v-deep .v-autocomplete-list,
.form-panel .autocomplete-shell.inactive ::v-deep .v-autocomplete-list {
  display: none;
  border: 0;
  box-shadow: none;
}

.form-panel.is-invalid {
  animation: shakeX 0.35s ease;
}

.form-panel ::v-deep .v-autocomplete.is-invalid input,
.form-panel ::v-deep .autocomplete-shell.is-invalid .v-autocomplete-input {
  border-color: rgba(231, 76, 60, 0.8) !important;
  box-shadow: 0 0 0 0.15rem rgba(231, 76, 60, 0.25);
}

.form-panel ::v-deep .v-autocomplete .v-autocomplete-list {
  background: #1b2126;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.form-panel ::v-deep .v-autocomplete .v-autocomplete-list-item {
  color: #fff;
  background: #1b2126;
}

.form-panel ::v-deep .v-autocomplete .v-autocomplete-list-item:hover,
.form-panel ::v-deep .v-autocomplete .v-autocomplete-list-item.v-autocomplete-item-active {
  background: rgba(255, 255, 255, 0.1);
}

.form-hint {
  display: block;
  margin-top: 6px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
}

.mini-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
}

.mini-action i {
  font-size: 0.75rem;
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(231, 76, 60, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.action-link:hover {
  color: #f78f8e;
  text-decoration: none;
}

.action-link i {
  font-size: 0.75rem;
}

.mini-icon-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.mini-icon-btn:hover {
  color: #f78f8e;
  border-color: rgba(247, 143, 142, 0.5);
  text-decoration: none;
}

@keyframes riseIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeSlide {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes driftIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseGlow {
  0%,
  100% {
    box-shadow: 0 0 18px rgba(231, 76, 60, 0.2);
  }
  50% {
    box-shadow: 0 0 26px rgba(231, 76, 60, 0.35);
  }
}

@keyframes shakeX {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-5px);
  }
  40% {
    transform: translateX(5px);
  }
  60% {
    transform: translateX(-3px);
  }
  80% {
    transform: translateX(3px);
  }
}
</style>
